import Preact from 'preact';
import * as Hooks from 'preact/hooks';
import * as Selectors from '../store/selectors';
import { useDispatch, useSelector } from './hooks/redux';
import Drawer from '@mui/material/Drawer';
import { SxProps } from '@mui/system';
import { TabsWithSwatches } from '@dora/components';
import * as ConfigurableProperties from '../store/configurableProperties';
import { FinishT } from '@dora/backpack/src/configurableProperties';
import { CurrentPropertySelections } from '../store/selectors/configurableProperties';

export type Props = {
    open: boolean;
    closeDrawer: () => void;
    loading: boolean;
    unavailableColours: Record<string, FinishT[]>;
    sx?: SxProps;
};

type SwatchProps = {
    label: string;
    active: boolean;
    warning: boolean;
    image?: string | undefined;
    colour?: string | undefined;
};
type TabContents = Record<string, SwatchProps[]>;

const isSwatchActive = (
    swatchName: string,
    activeTab: string,
    currentSelections: CurrentPropertySelections,
) => {
    const propertyBeingConfigured = currentSelections.find(sel => sel.propertyKey === activeTab);
    return swatchName === propertyBeingConfigured?.finish.name;
};

const isWarningRequired = (swatchName: string, unavailableColours: FinishT[] | undefined) => {
    return unavailableColours?.find(finish => finish.name === swatchName) !== undefined;
};

/**
 * Temporary Drawer used to display Global Configurable Properties for the current Set
 */
export function Component(props: Props) {
    const [activeTab, setActiveTab] = Hooks.useState<string | null>(null);

    const currentGlobalConfigurableProperties = useSelector(
        Selectors.ConfigurableProperties.configurableProperties,
    );

    const currentGlobalConfigurablePropertySelections = useSelector(
        Selectors.ConfigurableProperties.currentPropertySelections,
    );

    Hooks.useEffect(() => {
        if (currentGlobalConfigurableProperties && activeTab === null) {
            setActiveTab(currentGlobalConfigurableProperties[0].propertyKey);
        }
    }, [currentGlobalConfigurableProperties, activeTab]);

    const dispatch = useDispatch();

    const getTabContents = (activeTab: string) => {
        return currentGlobalConfigurableProperties.reduce((acc, prop) => {
            acc[prop.propertyKey] = prop.options.map(opt => ({
                label: opt.name,
                image: opt.image,
                warning: isWarningRequired(opt.name, props.unavailableColours[activeTab]),
                active: isSwatchActive(
                    opt.name,
                    activeTab,
                    currentGlobalConfigurablePropertySelections,
                ),
                colour: opt.colour,
            }));
            return acc;
        }, {} as TabContents);
    };

    return (
        <Drawer
            anchor="bottom"
            variant="temporary"
            open={props.open}
            onBackdropClick={() => {
                // drop back to the default tab
                setActiveTab(null);
                props.closeDrawer();
            }}
            sx={{
                height: '250px',
                '& .MuiDrawer-paper': {
                    height: '250px',
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: 'transparent',
                },
                ...props.sx,
            }}
        >
            {activeTab && (
                <TabsWithSwatches.Component
                    tabHeaders={currentGlobalConfigurableProperties.map(prop => ({
                        label: prop.label,
                        propertyKey: prop.propertyKey,
                    }))}
                    value={activeTab}
                    tabContents={getTabContents(activeTab)}
                    onSelectSwatch={swatchName => {
                        const propertyWithSwatchName = currentGlobalConfigurableProperties
                            .find(prop => prop.propertyKey === activeTab)
                            ?.options.find(opt => opt.name === swatchName);

                        if (propertyWithSwatchName) {
                            dispatch(
                                ConfigurableProperties.setConfigurablePropertySelection({
                                    propertyKey: activeTab,
                                    finish: propertyWithSwatchName,
                                }),
                            );
                        }
                    }}
                    onChangeTab={propertyKey => setActiveTab(propertyKey)}
                    loading={props.loading}
                />
            )}
        </Drawer>
    );
}
